/**
 * Sentry Logs + Spans Writer
 *
 * @module @chatsdet/diagnostic-workspace/sentry-writer
 * @description Pure-transform writer. Takes pre-fetched logs/spans (data-fetch
 * layer responsibility) and dumps them to `logs/sentry-logs.jsonl` /
 * `logs/sentry-spans.jsonl`. When the source was unavailable, the caller marks
 * the partial flag in `RunData.sentryLogs`/`sentrySpans`; we encode it as a
 * sentinel first line so the agent can detect via
 * `head -n 1 logs/sentry-logs.jsonl | jq '.partial'`.
 */

import * as fs from 'node:fs/promises';
import { resolveWorkspacePath, WORKSPACE_PATHS } from './paths.js';
import type {
  RunDataPartial,
  RunDataSentryLog,
  RunDataSentrySpan,
} from './run-data.js';

export interface SentryWriterInput {
  workspaceRoot: string;
  logs: { partial?: RunDataPartial; rows: ReadonlyArray<RunDataSentryLog> };
  spans: { partial?: RunDataPartial; spans: ReadonlyArray<RunDataSentrySpan> };
}

const writeJsonlWithSentinel = async (
  absolutePath: string,
  partial: RunDataPartial | undefined,
  rows: ReadonlyArray<unknown>,
): Promise<void> => {
  const lines: string[] = [];
  if (partial) lines.push(JSON.stringify(partial));
  for (const r of rows) lines.push(JSON.stringify(r));
  await fs.writeFile(
    absolutePath,
    lines.length === 0 ? '' : `${lines.join('\n')}\n`,
    'utf-8',
  );
};

/** Returns true when either source was partial. */
export const writeSentryLogsAndSpans = async (input: SentryWriterInput): Promise<boolean> => {
  await writeJsonlWithSentinel(
    resolveWorkspacePath(input.workspaceRoot, WORKSPACE_PATHS.logsSentry),
    input.logs.partial,
    input.logs.rows,
  );
  await writeJsonlWithSentinel(
    resolveWorkspacePath(input.workspaceRoot, WORKSPACE_PATHS.logsSentrySpans),
    input.spans.partial,
    input.spans.spans,
  );
  return Boolean(input.logs.partial || input.spans.partial);
};
